import { Amplify } from 'aws-amplify';

export interface RuntimeConfig {
  auth?: {
    userPoolId?: string;
    userPoolClientId?: string;
    domain?: string;
    signInRedirectTo?: string[];
    signOutRedirectTo?: string[];
  };
  api?: {
    url?: string;
    webApiUrl?: string;
  };
}

let cachedConfig: RuntimeConfig | null = null;
let inflight: Promise<RuntimeConfig> | null = null;
let amplifyConfigured = false;

const fetchJson = async <T>(url: string): Promise<T | null> => {
  try {
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) {
      return null;
    }
    return await response.json() as T;
  } catch (error) {
    console.warn('Failed to load configuration from', url, error);
    return null;
  }
};

type AuthResponse = {
  userPoolId?: string;
  userPoolClientId?: string;
  domain?: string;
  signInRedirectTo?: string[] | string;
  signOutRedirectTo?: string[] | string;
};

const normalizeRedirects = (value?: string[] | string): string[] | undefined => {
  if (Array.isArray(value)) {
    const filtered = value
      .map(entry => entry?.trim())
      .filter((entry): entry is string => Boolean(entry));
    return filtered.length ? filtered : undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    if (trimmed.includes(',')) {
      return trimmed.split(',').map(part => part.trim()).filter(Boolean);
    }
    return [trimmed];
  }

  return undefined;
};

const buildRedirectUrls = (paths: string[] | undefined, origin: string, fallback: string): string[] => {
  const normalizedOrigin = origin.replace(/\/$/, '');
  const values = paths?.length ? paths : [fallback];
  return values.map(path => {
    if (!path) {
      return `${normalizedOrigin}${fallback}`;
    }

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    if (path.startsWith('/')) {
      return `${normalizedOrigin}${path}`;
    }

    return `${normalizedOrigin}/${path}`;
  });
};

export const loadRuntimeConfig = async (): Promise<RuntimeConfig> => {
  if (cachedConfig) {
    return cachedConfig;
  }

  if (inflight) {
    return inflight;
  }

  inflight = (async () => {
    const [auth, api] = await Promise.all([
      fetchJson<AuthResponse>('/config.json'),
      fetchJson<{ url?: string; web_api_url?: string }>('/api-config.json')
    ]);

    const config: RuntimeConfig = {
      auth: auth
        ? {
            userPoolId: auth.userPoolId,
            userPoolClientId: auth.userPoolClientId,
            domain: auth.domain,
            signInRedirectTo: normalizeRedirects(auth.signInRedirectTo),
            signOutRedirectTo: normalizeRedirects(auth.signOutRedirectTo)
          }
        : undefined,
      api: api?.url || api?.web_api_url
        ? {
            url: api.url,
            webApiUrl: api.web_api_url ?? api.url
          }
        : undefined
    };

    const fallbackApi = import.meta.env.VITE_API_URL;
    if (!config.api?.url && typeof fallbackApi === 'string' && fallbackApi.length > 0) {
      config.api = { url: fallbackApi, webApiUrl: fallbackApi };
    }

    cachedConfig = config;
    inflight = null;
    return config;
  })();

  return inflight;
};

export const ensureAmplifyConfigured = async (): Promise<RuntimeConfig> => {
  const config = await loadRuntimeConfig();

  if (!amplifyConfigured && config.auth?.userPoolId && config.auth?.userPoolClientId) {
    const origin = window.location.origin;
    const oauthDomain = config.auth.domain?.replace(/^https?:\/\//, '') ?? undefined;
    const signInRedirects = buildRedirectUrls(config.auth.signInRedirectTo, origin, '/auth/callback');
    const signOutRedirects = buildRedirectUrls(config.auth.signOutRedirectTo, origin, '/');

    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: config.auth.userPoolId,
          userPoolClientId: config.auth.userPoolClientId,
          loginWith: {
            email: true,
            oauth: oauthDomain
              ? {
                  domain: oauthDomain,
                  scopes: ['email', 'openid', 'profile'],
                  redirectSignIn: signInRedirects,
                  redirectSignOut: signOutRedirects,
                  responseType: 'code'
                }
              : undefined
          }
        }
      }
    });

    amplifyConfigured = true;
  }

  return config;
};

export const resetRuntimeConfig = (): void => {
  cachedConfig = null;
  inflight = null;
  amplifyConfigured = false;
};
